import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Maximize2, Minimize2, Download, X, Settings, Plus, RotateCcw, Trash2, Eye, Lock } from "lucide-react";
import { unrealizedPnl, positionMargin, getLeverage, STATUS, ROLE } from "../../lib/paper/engine";
import { downloadCSV } from "../../lib/paper/csv";
import EngineAccountView, { EngineSummaryBar } from "./EngineAccountView";

// v2: the dock now starts collapsed to its summary bar (Markets redesign).
const DOCK_KEY = "aeon.paper.dock.v2";
const fmt = (p, dp = 2) => (p == null || Number.isNaN(p) ? "—" : p.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const fmt4 = (p) => fmt(p, 4);
const usd = (v) => (v == null || Number.isNaN(v) ? "—" : `${v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null || Number.isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`);
const sign = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "text-zinc-300");
const ts = (t) => (t ? new Date(t).toLocaleString(undefined, { hour12: false }) : "—");

const TABS = [
  { id: "positions", label: "Positions" },
  { id: "orders", label: "Orders" },
  { id: "orderHistory", label: "Order history" },
  { id: "balance", label: "Balance history" },
  { id: "activity", label: "Activity log" },
  { id: "trades", label: "Trade history" },
  { id: "analytics", label: "Analytics" },
];

function loadDock() {
  try {
    return { height: 260, collapsed: true, maximized: false, ...(JSON.parse(localStorage.getItem(DOCK_KEY) || "{}")) };
  } catch {
    return { height: 260, collapsed: true, maximized: false };
  }
}

// `engineView` = { summary, positions } when an autonomous-engine account is
// selected — the dock then renders it strictly read-only.
export default function PaperDock({
  engine, onEditPosition, onClosePosition, onAddAccount, onResetAccount, onDeleteAccount,
  engineAccounts = [], engineView = null, onSelectEngine, onSelectManual, onSelectSymbol,
}) {
  const { state, account, rollup, settings, lastPrices, actions } = engine;
  const [dock, setDock] = useState(loadDock);
  const [tab, setTab] = useState("positions");
  const [orderFilter, setOrderFilter] = useState("all");
  const [acctMenu, setAcctMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(DOCK_KEY, JSON.stringify(dock)); } catch {}
  }, [dock]);

  // ── resize by dragging the top edge ──
  const dragRef = useRef(null);
  const onResizeDown = (e) => {
    dragRef.current = { sy: e.clientY, h: dock.height };
    window.addEventListener("mousemove", onResizeMove);
    window.addEventListener("mouseup", onResizeUp);
    e.preventDefault();
  };
  const onResizeMove = (e) => {
    if (!dragRef.current) return;
    const h = Math.max(120, Math.min(600, dragRef.current.h + (dragRef.current.sy - e.clientY)));
    setDock((d) => ({ ...d, height: h, collapsed: false }));
  };
  const onResizeUp = () => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onResizeMove);
    window.removeEventListener("mouseup", onResizeUp);
  };
  useEffect(() => () => onResizeUp(), []);

  // ── derived data ──
  const positions = useMemo(() => Object.values(account.positions).filter((p) => p.qty > 0), [account.positions]);
  const orders = useMemo(() => Object.values(account.orders), [account.orders]);
  const working = orders.filter((o) => o.status === STATUS.WORKING || o.status === STATUS.INACTIVE);
  const closedOrders = orders.filter((o) => o.status !== STATUS.WORKING && o.status !== STATUS.INACTIVE);
  const closedTrades = useMemo(() => account.trades.filter((t) => t.realized !== 0), [account.trades]);

  const bracketFor = (symbol, role) => working.find((o) => o.symbol === symbol && o.role === role);

  const posRow = (p) => {
    const last = lastPrices[p.symbol] ?? p.avgPrice;
    const lev = getLeverage(account, p.symbol, settings);
    const uPnl = unrealizedPnl(p, last);
    const notional = Math.abs(p.qty) * p.avgPrice;
    const marketVal = Math.abs(p.qty) * last;
    const uPnlPct = notional ? (uPnl / (notional / lev)) * 100 : 0;
    return { p, last, lev, uPnl, notional, marketVal, uPnlPct,
      tp: bracketFor(p.symbol, ROLE.TP)?.limitPrice, sl: bracketFor(p.symbol, ROLE.SL)?.stopPrice,
      margin: positionMargin(p, lev) };
  };

  // orders sub-filter
  const filterCounts = {
    all: orders.length,
    working: orders.filter((o) => o.status === STATUS.WORKING).length,
    inactive: orders.filter((o) => o.status === STATUS.INACTIVE).length,
    filled: orders.filter((o) => o.status === STATUS.FILLED).length,
    cancelled: orders.filter((o) => o.status === STATUS.CANCELLED).length,
    rejected: orders.filter((o) => o.status === STATUS.REJECTED).length,
  };
  const shownOrders = orderFilter === "all" ? orders : orders.filter((o) => o.status === orderFilter);

  // analytics
  const analytics = useMemo(() => {
    const wins = closedTrades.filter((t) => t.realized > 0);
    const losses = closedTrades.filter((t) => t.realized < 0);
    const grossProfit = wins.reduce((s, t) => s + t.realized, 0);
    const grossLoss = Math.abs(losses.reduce((s, t) => s + t.realized, 0));
    const n = closedTrades.length;
    let cum = 0;
    const curve = [...closedTrades].reverse().map((t) => { cum += t.realized; return cum; });
    return {
      n, wins: wins.length,
      winPct: n ? (wins.length / n) * 100 : 0,
      profitFactor: grossLoss ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0,
      expectancy: n ? closedTrades.reduce((s, t) => s + t.realized, 0) / n : 0,
      curve,
    };
  }, [closedTrades]);

  const activeAccounts = Object.values(state.accounts);
  const metric = (label, value, cls = "text-zinc-100") => (
    <div className="px-3">
      <div className="text-[11px] text-zinc-500 whitespace-nowrap">{label}</div>
      <div className={`font-mono tabular-nums text-sm ${cls}`}>{value}</div>
    </div>
  );

  const bodyHeight = dock.maximized ? "70vh" : dock.height;

  return (
    <div className="border border-zinc-800 rounded-lg bg-zinc-900/60 mt-2 flex flex-col overflow-hidden">
      {/* resize handle */}
      {!dock.collapsed && <div onMouseDown={onResizeDown} className="h-1 cursor-ns-resize bg-zinc-800 hover:bg-emerald-600/50" />}

      {/* header */}
      <div className="flex items-center gap-2 px-3 border-b border-zinc-800 relative" style={{ height: 44 }}>
        <button onClick={() => setAcctMenu((m) => !m)} className="flex items-center gap-1.5 text-sm font-semibold text-zinc-100 whitespace-nowrap">
          {engineView ? (
            <>
              <span>{engineView.summary.emoji}</span> {engineView.summary.name}
              <span className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300"><Lock className="w-3 h-3" /> View only</span>
            </>
          ) : (
            <span title="Your manual paper-trading sim. Separate from AEON's engine accounts (LAB, PERSONAL, REAL_LIFE, RESEARCH), whose positions are in the Positions list.">
              Manual paper · {account.name}
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        {acctMenu && (
          <div className="absolute top-full left-3 z-30 mt-1 bg-zinc-900 border border-zinc-700 rounded shadow-lg text-xs w-64 max-h-[70vh] overflow-y-auto">
            <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-wide text-zinc-500">My accounts</div>
            {activeAccounts.map((a) => (
              <button key={a.id} onClick={() => { onSelectManual?.(a.id); setAcctMenu(false); }}
                className={`block w-full text-left px-3 py-1.5 hover:bg-zinc-800 ${!engineView && a.id === account.id ? "text-emerald-400" : "text-zinc-200"}`}>
                {a.name}
              </button>
            ))}
            <button onClick={() => { setShowNew(true); setAcctMenu(false); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 hover:bg-zinc-800 text-emerald-300">
              <Plus className="w-3 h-3" /> New account
            </button>
            {!engineView && (
              <>
                <button onClick={() => { onResetAccount?.(account.id); setAcctMenu(false); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 hover:bg-zinc-800 text-amber-300">
                  <RotateCcw className="w-3 h-3" /> Reset “{account.name}”
                </button>
                {activeAccounts.length > 1 && (
                  <button onClick={() => { onDeleteAccount?.(account.id); setAcctMenu(false); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 hover:bg-zinc-800 text-rose-300">
                    <Trash2 className="w-3 h-3" /> Delete “{account.name}”
                  </button>
                )}
                <button onClick={() => { setShowSettings(true); setAcctMenu(false); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 hover:bg-zinc-800">
                  <Settings className="w-3 h-3" /> Settings
                </button>
              </>
            )}
            <div className="border-t border-zinc-800 mt-1" />
            <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-wide text-zinc-500 flex items-center gap-1"><Eye className="w-3 h-3" /> AEON accounts · view only</div>
            {engineAccounts.length === 0 && <div className="px-3 py-1.5 text-zinc-500">Loading…</div>}
            {engineAccounts.map((a) => (
              <button key={a.account_id} onClick={() => { onSelectEngine?.(a.account_id); setAcctMenu(false); }}
                className={`flex items-center justify-between w-full text-left px-3 py-1.5 hover:bg-zinc-800 ${engineView?.summary.account_id === a.account_id ? "text-sky-300" : "text-zinc-200"}`}>
                <span>{a.emoji} {a.name}</span>
                <span className="text-[10px] text-zinc-500">{a.open_positions || 0} open</span>
              </button>
            ))}
          </div>
        )}

        {/* summary bar */}
        {engineView ? <EngineSummaryBar summary={engineView.summary} metric={metric} /> : (
        <div className="flex-1 flex items-center overflow-x-auto no-scrollbar divide-x divide-zinc-800 ml-2">
          {metric("Balance", usd(rollup.balance))}
          {metric("Equity", usd(rollup.equity))}
          {metric("Realized", usd(rollup.realizedPnl), sign(rollup.realizedPnl))}
          {metric("Unrealized", usd(rollup.unrealizedPnl), sign(rollup.unrealizedPnl))}
          {metric("Acct margin", usd(rollup.accountMargin))}
          {metric("Avail funds", usd(rollup.availableFunds))}
          {metric("Orders margin", usd(rollup.ordersMargin))}
          {metric("Buffer", pct(rollup.marginBuffer * 100), sign(rollup.marginBuffer))}
        </div>
        )}

        <div className="flex items-center gap-1">
          <button onClick={() => setDock((d) => ({ ...d, maximized: !d.maximized, collapsed: false }))} className="p-1 hover:bg-zinc-800 rounded" title="Maximize">
            {dock.maximized ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setDock((d) => ({ ...d, collapsed: !d.collapsed }))} className="p-1 hover:bg-zinc-800 rounded" title="Collapse">
            {dock.collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {!dock.collapsed && engineView && (
        <div className="overflow-auto text-xs" style={{ height: bodyHeight }}>
          <EngineAccountView positions={engineView.positions} onSelectSymbol={onSelectSymbol} />
        </div>
      )}

      {!dock.collapsed && !engineView && (
        <>
          {/* tabs */}
          <div className="flex items-center gap-1 px-3 py-1.5 border-b border-zinc-800 overflow-x-auto no-scrollbar">
            {TABS.map((t) => {
              const count = t.id === "positions" ? positions.length : t.id === "orders" ? working.length : t.id === "trades" ? closedTrades.length : null;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap ${tab === t.id ? "bg-emerald-600 text-white" : "bg-zinc-800/60 text-zinc-400 hover:text-white"}`}>
                  {t.label}{count != null ? ` (${count})` : ""}
                </button>
              );
            })}
            <button onClick={() => exportTab(tab, { positions: positions.map(posRow), shownOrders, closedOrders, trades: account.trades, activity: account.activity, analytics })}
              className="ml-auto p-1 text-zinc-400 hover:text-white" title="Export CSV">
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* body */}
          <div className="overflow-auto text-xs" style={{ height: bodyHeight }}>
            {tab === "positions" && (
              positions.length === 0 ? <Empty msg="There are no open positions in your trading account yet." /> : (
                <Table head={["Symbol", "Side", "Qty", "Avg fill", "TP", "SL", "Last", "uPnL", "uPnL %", "Trade value", "Market value", "Lev", "Margin", ""]}>
                  {positions.map(posRow).map((r) => (
                    <tr key={r.p.symbol} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <Td><span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">OKX:{r.p.symbol}USDT.P</span></Td>
                      <Td><span className={r.p.side === "long" ? "text-emerald-400" : "text-rose-400"}>{r.p.side === "long" ? "Long" : "Short"}</span></Td>
                      <Td mono>{fmt4(r.p.qty)}</Td>
                      <Td mono>{fmt(r.p.avgPrice)}</Td>
                      <Td mono>{r.tp ? fmt(r.tp) : "—"}</Td>
                      <Td mono>{r.sl ? fmt(r.sl) : "—"}</Td>
                      <Td mono>{fmt(r.last)}</Td>
                      <Td mono cls={sign(r.uPnl)}>{usd(r.uPnl)}</Td>
                      <Td mono cls={sign(r.uPnlPct)}>{pct(r.uPnlPct)}</Td>
                      <Td mono>{usd(r.notional)}</Td>
                      <Td mono>{usd(r.marketVal)}</Td>
                      <Td mono>{r.lev}x</Td>
                      <Td mono>{usd(r.margin)}</Td>
                      <Td>
                        <div className="flex gap-1">
                          <button onClick={() => onEditPosition?.(r.p.symbol)} className="px-1.5 py-0.5 bg-zinc-800 rounded hover:bg-zinc-700">✎</button>
                          <button onClick={() => onClosePosition?.(r.p)} className="px-1.5 py-0.5 bg-rose-600/80 rounded text-white hover:bg-rose-500">✕</button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </Table>
              )
            )}

            {tab === "orders" && (
              <>
                <div className="flex gap-1 px-2 py-1.5">
                  {["all", "working", "inactive", "filled", "cancelled", "rejected"].map((f) => (
                    <button key={f} onClick={() => setOrderFilter(f)}
                      className={`px-2 py-0.5 rounded-full text-[10px] capitalize ${orderFilter === f ? "bg-zinc-700 text-white" : "bg-zinc-800/50 text-zinc-400"}`}>
                      {f} ({filterCounts[f] ?? 0})
                    </button>
                  ))}
                </div>
                {shownOrders.length === 0 ? <Empty msg="No orders." /> : (
                  <Table head={["Symbol", "Side", "Type", "Qty", "Limit/Stop", "Fill", "Status", "Comm", "Placed", ""]}>
                    {shownOrders.map((o) => (
                      <tr key={o.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                        <Td>OKX:{o.symbol}USDT.P</Td>
                        <Td cls={o.side === "buy" ? "text-emerald-400" : "text-rose-400"}>{o.side}</Td>
                        <Td>{o.type}{o.role ? ` · ${o.role.toUpperCase()}` : ""}</Td>
                        <Td mono>{fmt4(o.qty)}</Td>
                        <Td mono>{o.limitPrice ? fmt(o.limitPrice) : o.stopPrice ? fmt(o.stopPrice) : "—"}</Td>
                        <Td mono>{o.fillPrice ? fmt(o.fillPrice) : "—"}</Td>
                        <Td><StatusPill s={o.status} /></Td>
                        <Td mono>{o.commission ? usd(o.commission) : "—"}</Td>
                        <Td>{ts(o.placedAt)}</Td>
                        <Td>{(o.status === STATUS.WORKING || o.status === STATUS.INACTIVE) && (
                          <button onClick={() => actions.cancelOrder(o.id)} className="px-1.5 py-0.5 bg-zinc-800 rounded hover:bg-zinc-700">cancel</button>
                        )}</Td>
                      </tr>
                    ))}
                  </Table>
                )}
              </>
            )}

            {tab === "orderHistory" && (
              closedOrders.length === 0 ? <Empty msg="No order history." /> : (
                <Table head={["Symbol", "Side", "Type", "Qty", "Fill", "Status", "Reason", "Time"]}>
                  {closedOrders.map((o) => (
                    <tr key={o.id} className="border-b border-zinc-800/50">
                      <Td>OKX:{o.symbol}USDT.P</Td>
                      <Td>{o.side}</Td>
                      <Td>{o.type}</Td>
                      <Td mono>{fmt4(o.qty)}</Td>
                      <Td mono>{o.fillPrice ? fmt(o.fillPrice) : "—"}</Td>
                      <Td><StatusPill s={o.status} /></Td>
                      <Td cls="text-zinc-500">{o.reason || "—"}</Td>
                      <Td>{ts(o.filledAt || o.placedAt)}</Td>
                    </tr>
                  ))}
                </Table>
              )
            )}

            {tab === "balance" && (
              closedTrades.length === 0 ? <Empty msg="No balance changes yet." /> : (
                <Table head={["Time", "Symbol", "Realized", "Running balance"]}>
                  {(() => { let run = account.startingBalance; const rows = [...closedTrades].reverse().map((t) => { run += t.realized; return { t, run }; }).reverse();
                    return rows.map(({ t, run }) => (
                      <tr key={t.id} className="border-b border-zinc-800/50">
                        <Td>{ts(t.t)}</Td><Td>OKX:{t.symbol}USDT.P</Td>
                        <Td mono cls={sign(t.realized)}>{usd(t.realized)}</Td>
                        <Td mono>{usd(run)}</Td>
                      </tr>
                    )); })()}
                </Table>
              )
            )}

            {tab === "activity" && (
              account.activity.length === 0 ? <Empty msg="No activity yet." /> : (
                <Table head={["Time", "Type", "Message"]}>
                  {account.activity.map((e) => (
                    <tr key={e.id} className="border-b border-zinc-800/50">
                      <Td>{ts(e.t)}</Td><Td cls="text-zinc-400">{e.type}</Td><Td>{e.message}</Td>
                    </tr>
                  ))}
                </Table>
              )
            )}

            {tab === "trades" && (
              closedTrades.length === 0 ? <Empty msg="No closed trades yet." /> : (
                <Table head={["Time", "Symbol", "Side", "Type", "Price", "Size", "Net PnL"]}>
                  {closedTrades.map((t) => (
                    <tr key={t.id} className="border-b border-zinc-800/50">
                      <Td>{ts(t.t)}</Td>
                      <Td>OKX:{t.symbol}USDT.P</Td>
                      <Td cls={t.side === "buy" ? "text-emerald-400" : "text-rose-400"}>{t.side}</Td>
                      <Td>{t.type}</Td>
                      <Td mono>{fmt(t.price)}</Td>
                      <Td mono>{fmt4(t.qty)} / {(t.notional / 1000).toFixed(2)}K</Td>
                      <Td mono cls={sign(t.realized)}>{usd(t.realized)}</Td>
                    </tr>
                  ))}
                </Table>
              )
            )}

            {tab === "analytics" && (
              <div className="p-3 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Stat label="Profitable trades" value={`${analytics.winPct.toFixed(0)}%`} sub={`${analytics.wins}/${analytics.n}`} />
                  <Stat label="Profit factor" value={analytics.profitFactor === Infinity ? "∞" : analytics.profitFactor.toFixed(2)} />
                  <Stat label="Expectancy" value={usd(analytics.expectancy)} cls={sign(analytics.expectancy)} />
                  <Stat label="Closed trades" value={analytics.n} />
                </div>
                <CumCurve data={analytics.curve} />
              </div>
            )}
          </div>
        </>
      )}

      {showSettings && <SettingsModal engine={engine} onClose={() => setShowSettings(false)} />}
      {showNew && <NewAccountModal onCreate={onAddAccount} onClose={() => setShowNew(false)} />}
    </div>
  );
}

// ── small presentational helpers ──
const Empty = ({ msg }) => <div className="p-6 text-center text-zinc-500">{msg}</div>;
const Td = ({ children, mono, cls = "" }) => <td className={`px-2 py-1.5 whitespace-nowrap ${mono ? "font-mono tabular-nums" : ""} ${cls}`}>{children}</td>;
const Table = ({ head, children }) => (
  <table className="w-full text-left">
    <thead className="sticky top-0 bg-zinc-900 text-[10px] uppercase text-zinc-500">
      <tr>{head.map((h, i) => <th key={i} className="px-2 py-1.5 font-medium whitespace-nowrap">{h}</th>)}</tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
);
const Stat = ({ label, value, sub, cls = "text-zinc-100" }) => (
  <div className="bg-zinc-800/40 rounded p-3">
    <div className="text-[10px] uppercase text-zinc-500">{label}</div>
    <div className={`text-lg font-mono tabular-nums ${cls}`}>{value}</div>
    {sub && <div className="text-[10px] text-zinc-500">{sub}</div>}
  </div>
);
const StatusPill = ({ s }) => {
  const map = { working: "text-sky-400", inactive: "text-zinc-400", filled: "text-emerald-400", cancelled: "text-zinc-500", rejected: "text-rose-400" };
  return <span className={`capitalize ${map[s] || ""}`}>{s}</span>;
};

function CumCurve({ data }) {
  if (!data || data.length < 2) return <div className="h-32 flex items-center justify-center text-zinc-500">Cumulative PnL appears after 2+ closed trades.</div>;
  const W = 600, H = 120, pad = 4;
  const min = Math.min(0, ...data), max = Math.max(0, ...data);
  const range = max - min || 1;
  const x = (i) => pad + (i / (data.length - 1)) * (W - 2 * pad);
  const y = (v) => H - pad - ((v - min) / range) * (H - 2 * pad);
  const path = data.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const last = data[data.length - 1];
  return (
    <div>
      <div className="text-[10px] uppercase text-zinc-500 mb-1">Cumulative PnL</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 120 }} preserveAspectRatio="none">
        <line x1={pad} y1={y(0)} x2={W - pad} y2={y(0)} stroke="#3f3f46" strokeWidth="1" />
        <path d={path} fill="none" stroke={last >= 0 ? "#22ab94" : "#f23645"} strokeWidth="1.5" />
      </svg>
    </div>
  );
}

function SettingsModal({ engine, onClose }) {
  const { settings, account, actions } = engine;
  const [start, setStart] = useState(account.startingBalance);
  const [taker, setTaker] = useState(settings.takerFeeRate * 100);
  const [slip, setSlip] = useState(settings.slippageBps);
  const [lev, setLev] = useState(settings.defaultLeverage);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-[320px] p-4 text-xs space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><span className="font-semibold text-zinc-100">Paper trading settings</span><button onClick={onClose}><X className="w-4 h-4" /></button></div>
        <Field label="Starting balance (USD)" value={start} onChange={setStart} />
        <Field label="Taker fee (%)" value={taker} onChange={setTaker} step="0.01" />
        <Field label="Slippage (bps)" value={slip} onChange={setSlip} />
        <Field label="Default leverage" value={lev} onChange={setLev} />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 bg-zinc-800 rounded">Cancel</button>
          <button onClick={() => {
            actions.updateSettings({ takerFeeRate: Number(taker) / 100, slippageBps: Number(slip), defaultLeverage: Number(lev) });
            if (Number(start) !== account.startingBalance) actions.resetAccount(account.id, { startingBalance: Number(start) });
            onClose();
          }} className="px-3 py-1.5 bg-emerald-600 text-white rounded">Save</button>
        </div>
      </div>
    </div>
  );
}
function NewAccountModal({ onCreate, onClose }) {
  const [name, setName] = useState("");
  const [start, setStart] = useState(10000);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const bal = Number(start);
    if (!name.trim()) return setErr("Give the account a name.");
    if (!(bal >= 10 && bal <= 100000000)) return setErr("Starting balance must be between $10 and $100,000,000.");
    setBusy(true);
    setErr(null);
    try {
      await onCreate?.({ name: name.trim().slice(0, 40), startingBalance: bal });
      onClose();
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex?.message || "Couldn't create the account.");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <form onSubmit={submit} className="bg-zinc-900 border border-zinc-700 rounded-lg w-[320px] p-4 text-xs space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><span className="font-semibold text-zinc-100">New paper account</span><button type="button" onClick={onClose}><X className="w-4 h-4" /></button></div>
        <label className="block">
          <span className="text-[10px] text-zinc-500">Name</span>
          <input autoFocus maxLength={40} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Scalps"
            className="w-full bg-zinc-800 rounded px-2 py-1.5 outline-none mt-0.5" />
        </label>
        <Field label="Starting balance (USD)" value={start} onChange={setStart} />
        {err && <div className="text-rose-400">{err}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 bg-zinc-800 rounded">Cancel</button>
          <button type="submit" disabled={busy} className="px-3 py-1.5 bg-emerald-600 text-white rounded disabled:opacity-50">{busy ? "Creating…" : "Create"}</button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, step }) => (
  <label className="block">
    <span className="text-[10px] text-zinc-500">{label}</span>
    <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-zinc-800 rounded px-2 py-1.5 outline-none font-mono mt-0.5" />
  </label>
);

// CSV export per tab
function exportTab(tab, data) {
  const map = {
    positions: () => downloadCSV("paper_positions.csv", data.positions, [
      { label: "Symbol", get: (r) => r.p.symbol }, { label: "Side", get: (r) => r.p.side },
      { label: "Qty", get: (r) => r.p.qty }, { label: "AvgFill", get: (r) => r.p.avgPrice },
      { label: "Last", get: (r) => r.last }, { label: "uPnL", get: (r) => r.uPnl },
      { label: "Leverage", get: (r) => r.lev }, { label: "Margin", get: (r) => r.margin },
    ]),
    orders: () => downloadCSV("paper_orders.csv", data.shownOrders, [
      { label: "Symbol", key: "symbol" }, { label: "Side", key: "side" }, { label: "Type", key: "type" },
      { label: "Qty", key: "qty" }, { label: "Limit", key: "limitPrice" }, { label: "Stop", key: "stopPrice" },
      { label: "Fill", key: "fillPrice" }, { label: "Status", key: "status" }, { label: "Commission", key: "commission" },
    ]),
    trades: () => downloadCSV("paper_trades.csv", data.trades, [
      { label: "Time", get: (t) => new Date(t.t).toISOString() }, { label: "Symbol", key: "symbol" },
      { label: "Side", key: "side" }, { label: "Price", key: "price" }, { label: "Qty", key: "qty" },
      { label: "Notional", key: "notional" }, { label: "Realized", key: "realized" },
    ]),
    activity: () => downloadCSV("paper_activity.csv", data.activity, [
      { label: "Time", get: (e) => new Date(e.t).toISOString() }, { label: "Type", key: "type" }, { label: "Message", key: "message" },
    ]),
  };
  (map[tab] || map.trades)();
}
